import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePostApiUsersPasswordResetToken } from '@/api/generated/public/public'
import { isServerUnreachable } from '@/lib/apiError'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Eye, EyeOff, Info, Check, X } from 'lucide-react'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'

interface PasswordRule {
  label: string
  test: (pw: string) => boolean
}

function getPasswordRules(t: TFunction): PasswordRule[] {
  return [
    { label: t('passwordRules.minLength'), test: (pw) => pw.length >= 8 },
    { label: t('passwordRules.upperCase'), test: (pw) => /[A-Z]/.test(pw) },
    { label: t('passwordRules.lowerCase'), test: (pw) => /[a-z]/.test(pw) },
    { label: t('passwordRules.number'), test: (pw) => /[0-9]/.test(pw) },
    { label: t('passwordRules.specialChar'), test: (pw) => /[^A-Za-z0-9]/.test(pw) },
  ]
}

export default function ResetPasswordPage() {
  const { t } = useTranslation('login')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token') ?? ''
  const email = searchParams.get('email') ?? ''

  const [password, setPassword] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const passwordRules = getPasswordRules(t)
  const passwordValid = passwordRules.every((r) => r.test(password))

  const { mutate: resetPassword, isPending } = usePostApiUsersPasswordResetToken({
    mutation: {
      onSuccess: () => setSuccess(true),
      // Bei totem Backend nicht fälschlich „Link ungültig" behaupten.
      onError: (error) => setError(isServerUnreachable(error) ? t('serverUnreachable') : t('resetPasswordPage.errors.linkInvalid')),
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!passwordValid) {
      setError(t('resetPasswordPage.errors.passwordInvalid'))
      return
    }
    if (password !== passwordRepeat) {
      setError(t('resetPasswordPage.errors.passwordMismatch'))
      return
    }
    resetPassword({ token, data: { email, newPassword: password } })
  }

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{t('resetPasswordPage.title')}</CardTitle>
            <CardDescription>
              {success
                ? t('resetPasswordPage.descriptionSuccess')
                : t('resetPasswordPage.description')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('resetPasswordPage.successText')}
              </p>
              <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
                {t('resetPasswordPage.goToLogin')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('resetPasswordPage.email')}</Label>
                <Input id="email" type="email" value={email} disabled />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('resetPasswordPage.newPassword')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={passwordValid ? 'text-green-500' : password ? 'text-destructive' : 'text-muted-foreground'}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-3" align="end">
                      <p className="text-xs font-medium mb-2">{t('passwordRules.title')}</p>
                      <ul className="space-y-1">
                        {passwordRules.map((rule) => {
                          const ok = rule.test(password)
                          return (
                            <li key={rule.label} className="flex items-center gap-2 text-xs">
                              {ok
                                ? <Check className="h-3 w-3 text-green-500 shrink-0" />
                                : <X className="h-3 w-3 text-destructive shrink-0" />
                              }
                              <span className={ok ? 'text-green-600' : 'text-muted-foreground'}>
                                {rule.label}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordRepeat">{t('resetPasswordPage.passwordRepeat')}</Label>
                <div className="relative">
                  <Input
                    id="passwordRepeat"
                    type={showPasswordRepeat ? 'text' : 'password'}
                    required
                    value={passwordRepeat}
                    onChange={(e) => setPasswordRepeat(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswordRepeat ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" disabled={isPending} className="w-full">
                {isPending ? t('resetPasswordPage.submitting') : t('resetPasswordPage.submit')}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                  {t('resetPasswordPage.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </WallpaperBackground>
  )
}
